import { useNavigate } from 'react-router-dom'
import { useState, useMemo, useEffect } from 'react'
import { supabase } from '@/integrations/supabase/client'
import Header from '@/components/Header'
import Footer from '@/components/Footer'
import WhatsAppFloat from '@/components/WhatsAppFloat'
import Breadcrumb from '@/components/Breadcrumb'
import CTABanner from '@/components/CTABanner'
import MetaTags from '@/components/MetaTags'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Calendar, User, ArrowRight, Search, X, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { format } from 'date-fns'
import { ptBR } from 'date-fns/locale'

interface BlogPost {
  slug: string
  title: string
  excerpt: string | null
  created_at: string
  author: string
  category: string
  image_url: string | null
  video_url: string | null
}

const getYouTubeVideoId = (url: string): string | null => {
  const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/
  const match = url.match(regExp)
  return match && match[2].length === 11 ? match[2] : null
}

const getYouTubeThumbnail = (videoUrl: string): string | null => {
  const videoId = getYouTubeVideoId(videoUrl)
  return videoId
    ? `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`
    : null
}

const Blog = () => {
  const navigate = useNavigate()
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedCategory, setSelectedCategory] = useState<string>('all')
  const [selectedAuthor, setSelectedAuthor] = useState<string>('all')
  const [selectedMonth, setSelectedMonth] = useState<string>('all')
  const [blogPosts, setBlogPosts] = useState<BlogPost[]>([])
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    fetchPosts()
  }, [])

  const fetchPosts = async () => {
    setIsLoading(true)
    const { data, error } = await supabase
      .from('blog_posts')
      .select(
        'slug, title, excerpt, created_at, author, category, image_url, video_url',
      )
      .eq('published', true)
      .order('created_at', { ascending: false })

    if (error) {
      console.error('Error fetching posts:', error)
    } else {
      setBlogPosts(data || [])
    }
    setIsLoading(false)
  }

  const categories = useMemo(() => {
    const cats = Array.from(new Set(blogPosts.map(post => post.category)))
    return ['all', ...cats]
  }, [blogPosts])

  const authors = useMemo(() => {
    const auth = Array.from(new Set(blogPosts.map(post => post.author)))
    return ['all', ...auth]
  }, [blogPosts])

  const months = useMemo(() => {
    const monthsList = Array.from(
      new Set(
        blogPosts.map(post => {
          const date = new Date(post.created_at)
          return format(date, 'MMMM yyyy', { locale: ptBR })
        }),
      ),
    )
    return ['all', ...monthsList]
  }, [blogPosts])

  const filteredPosts = useMemo(() => {
    return blogPosts.filter(post => {
      const matchesSearch =
        searchTerm === '' ||
        post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (post.excerpt &&
          post.excerpt.toLowerCase().includes(searchTerm.toLowerCase()))

      const matchesCategory =
        selectedCategory === 'all' || post.category === selectedCategory
      const matchesAuthor =
        selectedAuthor === 'all' || post.author === selectedAuthor

      const postMonth = format(new Date(post.created_at), 'MMMM yyyy', {
        locale: ptBR,
      })
      const matchesMonth =
        selectedMonth === 'all' || postMonth === selectedMonth

      return matchesSearch && matchesCategory && matchesAuthor && matchesMonth
    })
  }, [searchTerm, selectedCategory, selectedAuthor, selectedMonth, blogPosts])

  const clearFilters = () => {
    setSearchTerm('')
    setSelectedCategory('all')
    setSelectedAuthor('all')
    setSelectedMonth('all')
  }

  const hasActiveFilters =
    searchTerm !== '' ||
    selectedCategory !== 'all' ||
    selectedAuthor !== 'all' ||
    selectedMonth !== 'all'

  const formatDate = (dateStr: string) => {
    return format(new Date(dateStr), "d 'de' MMMM, yyyy", { locale: ptBR })
  }

  return (
    <div className="min-h-screen flex flex-col">
      <MetaTags
        title="Blog - FotoLivro Store | Dicas e Inspirações sobre Fotolivros"
        description="Descubra dicas, tendências e inspirações sobre fotolivros personalizados. Aprenda a escolher as melhores fotos e preservar suas memórias mais preciosas."
        type="website"
      />
      <Header />
      <Breadcrumb />
      <main className="flex-1">
        <section className="py-20 bg-background">
          <div className="container mx-auto px-4">
            <h1 className="text-4xl md:text-5xl font-bold text-center mb-4 text-foreground">
              Blog FotoLivro Store
            </h1>
            <p className="text-center text-muted-foreground mb-12 max-w-2xl mx-auto">
              Dicas, inspirações e histórias sobre fotolivros e preservação de
              memórias
            </p>

            {/* Search and Filters */}
            <div className="max-w-5xl mx-auto mb-12 space-y-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  type="text"
                  placeholder="Buscar posts por título ou conteúdo..."
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  className="pl-10 pr-10"
                />
                {searchTerm && (
                  <button
                    onClick={() => setSearchTerm('')}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Select
                  value={selectedCategory}
                  onValueChange={setSelectedCategory}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Categoria" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todas as categorias</SelectItem>
                    {categories
                      .filter(cat => cat !== 'all')
                      .map(category => (
                        <SelectItem key={category} value={category}>
                          {category}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>

                <Select
                  value={selectedAuthor}
                  onValueChange={setSelectedAuthor}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Autor" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos os autores</SelectItem>
                    {authors
                      .filter(auth => auth !== 'all')
                      .map(author => (
                        <SelectItem key={author} value={author}>
                          {author}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>

                <Select value={selectedMonth} onValueChange={setSelectedMonth}>
                  <SelectTrigger>
                    <SelectValue placeholder="Mês" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos os meses</SelectItem>
                    {months
                      .filter(month => month !== 'all')
                      .map(month => (
                        <SelectItem key={month} value={month}>
                          {month}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>

              {hasActiveFilters && (
                <div className="flex justify-center">
                  <Button
                    variant="outline"
                    onClick={clearFilters}
                    className="gap-2"
                  >
                    <X className="h-4 w-4" />
                    Limpar filtros
                  </Button>
                </div>
              )}

              <p className="text-center text-muted-foreground text-sm">
                {filteredPosts.length}{' '}
                {filteredPosts.length === 1
                  ? 'post encontrado'
                  : 'posts encontrados'}
              </p>
            </div>

            {isLoading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin" />
              </div>
            ) : filteredPosts.length > 0 ? (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                {filteredPosts.map(post => (
                  <Card
                    key={post.slug}
                    className="overflow-hidden hover:shadow-lg transition-shadow cursor-pointer group"
                    onClick={() => navigate(`/blog/${post.slug}`)}
                  >
                    <div className="relative h-48 overflow-hidden">
                      <img
                        src={
                          post.image_url ||
                          (post.video_url &&
                            getYouTubeThumbnail(post.video_url)) ||
                          'https://images.unsplash.com/photo-1516979187457-637abb4f9353?w=800&h=500&fit=crop'
                        }
                        alt={post.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                      <Badge className="absolute top-4 left-4 bg-primary text-primary-foreground">
                        {post.category}
                      </Badge>
                    </div>
                    <CardHeader>
                      <CardTitle className="text-xl font-cormorant group-hover:text-primary transition-colors">
                        {post.title}
                      </CardTitle>
                      <CardDescription className="flex items-center gap-4 text-sm">
                        <span className="flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          {formatDate(post.created_at)}
                        </span>
                        <span className="flex items-center gap-1">
                          <User className="h-3 w-3" />
                          {post.author}
                        </span>
                      </CardDescription>
                    </CardHeader>
                    <CardContent>
                      <p className="text-muted-foreground mb-4">
                        {post.excerpt || 'Leia mais sobre este artigo...'}
                      </p>
                      <Button
                        variant="link"
                        className="p-0 h-auto group-hover:gap-2 transition-all"
                      >
                        Ler mais
                        <ArrowRight className="h-4 w-4 ml-1" />
                      </Button>
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <p className="text-muted-foreground text-lg mb-4">
                  {blogPosts.length === 0
                    ? 'Nenhum post publicado ainda.'
                    : 'Nenhum post encontrado com os filtros selecionados.'}
                </p>
                {hasActiveFilters && (
                  <Button onClick={clearFilters}>Limpar filtros</Button>
                )}
              </div>
            )}
          </div>
        </section>

        <div className="container mx-auto px-4 pb-12">
          <CTABanner
            title="Inspire-se e crie seu fotolivro"
            description="Veja nossos produtos e comece a preservar suas memórias agora"
            buttonText="Ver Produtos"
            buttonLink="/#produtos"
          />
        </div>
      </main>
      <Footer />
      <WhatsAppFloat />
    </div>
  )
}

export default Blog
